// components/NotificationBell.tsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Bell, Check, Trash2, Car, Users, Info, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import { AppNotification } from '../../services/notification.service';

const TYPE_ICON = {
  lead: Users,
  vehicle: Car,
  system: Info,
  message: MessageSquare,
} as const;

const TYPE_COLOR = {
  lead: 'bg-[#22C55E]/10 text-[#22C55E]',
  vehicle: 'bg-[#1E6DFF]/10 text-[#1E6DFF]',
  system: 'bg-[#717182]/10 text-[#717182]',
  message: 'bg-[#F59E0B]/10 text-[#F59E0B]',
} as const;

function timeAgo(iso: string, t: any): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('notifications.time.justNow', 'just now');
  if (mins < 60) return t('notifications.time.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('notifications.time.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  return t('notifications.time.daysAgo', { count: days });
}

export function NotificationBell() {
  const { t } = useTranslation('auth');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    items, unreadCount, loading, hasMore, filter, setFilter,
    markRead, markAllRead, removeOne, loadMore,
  } = useNotifications();

  // close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // esc closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const onItemClick = async (n: AppNotification) => {
    if (!n.isRead) await markRead(n.id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-xl hover:bg-[#F3F4F6] transition-colors"
        aria-label={t('notifications.title', 'Notifications')}
      >
        <Bell className="w-5 h-5 text-[#2D323A]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-[#E5E7EB] shadow-xl z-50 overflow-hidden">
          {/* header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-medium text-[#2D323A]">
              {t('notifications.title', 'Notifications')}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#1E6DFF] hover:underline flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                {t('notifications.markAllRead', 'Mark all read')}
              </button>
            )}
          </div>

          {/* filters */}
          <div className="flex gap-2 px-4 py-2 border-b border-[#F3F4F6]">
            {(['all', 'unread'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                  filter === f
                    ? 'bg-[#1E6DFF] text-white'
                    : 'bg-[#F3F4F6] text-[#717182] hover:bg-[#E5E7EB]'
                }`}
              >
                {t(`notifications.filter.${f}`, f)}
              </button>
            ))}
          </div>

          {/* list */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-9 h-9 bg-gray-200 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <p className="p-8 text-center text-sm text-[#717182]">
                {t('notifications.empty', 'No notifications yet')}
              </p>
            ) : (
              items.map((n) => {
                const Icon = TYPE_ICON[n.type] ?? Info;
                const color = TYPE_COLOR[n.type] ?? TYPE_COLOR.system;
                const content = (
                  <div
                    className={`flex gap-3 p-4 border-b border-[#F3F4F6] group cursor-pointer hover:bg-[#F9FAFB] ${
                      !n.isRead ? 'bg-[#1E6DFF]/[0.03]' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p className={`text-sm flex-1 ${!n.isRead ? 'text-[#2D323A] font-medium' : 'text-[#2D323A]'}`}>
                          {n.title}
                        </p>
                        {!n.isRead && <span className="w-2 h-2 rounded-full bg-[#1E6DFF] mt-1.5 flex-shrink-0" />}
                      </div>
                      {n.body && (
                        <p className="text-xs text-[#717182] mt-0.5 line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-[11px] text-[#717182] mt-1">{timeAgo(n.createdAt, t)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeOne(n.id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#F3F4F6] rounded-lg transition-all"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-[#717182]" />
                    </button>
                  </div>
                );

                return n.link ? (
                  <Link key={n.id} to={n.link} onClick={() => onItemClick(n)}>
                    {content}
                  </Link>
                ) : (
                  <div key={n.id} onClick={() => onItemClick(n)}>{content}</div>
                );
              })
            )}

            {hasMore && (
              <button
                onClick={loadMore}
                className="w-full py-2 text-xs text-[#1E6DFF] hover:bg-[#F9FAFB]"
              >
                {t('notifications.loadMore', 'Load more')}
              </button>
            )}
          </div>

          {/* footer */}
          {/* <Link
            to="/dealer/notifications"
            onClick={() => setOpen(false)}
            className="block px-4 py-3 text-center text-sm text-[#1E6DFF] border-t border-[#E5E7EB] hover:bg-[#F9FAFB]"
          >
            {t('notifications.viewAll', 'View all notifications')}
          </Link> */}
        </div>
      )}
    </div>
  );
}